import type { Item, ItemGroupPrice, PriceList } from '@erp/shared';
import { EyeOff, Pencil, Tags } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Card } from '../../../components/shadcn/index.js';
import { Button } from '../../../components/ui/index.js';
import { formatMoney } from '../../../lib/money.js';
import { listPriceLists } from '../../../lib/pricing.js';

import { fmtQty } from './profileFormat.js';
import { SectionTitle } from './ProfileParts.js';

interface Row {
  key: string;
  unitId: string | null;
  name: string;
  factor: number;
  hidden: boolean;
  salePrice: string | null;
  purchasePrice: string | null;
}

/**
 * Every price the product sells at, in one grid: each selling unit (the base
 * piece and every pack, with how many pieces it holds) against the normal
 * price and every active customer group. Each price is shown in ITS OWN
 * currency — a group priced in dollars stays in dollars — and a pack's price is
 * also read per piece, so a carton that is no cheaper than its pieces shows.
 * Read-only; the edit button opens the product form.
 */
export function ItemPricesTab({ item, groupPrices, pricingEnabled, canCost, canWrite }: {
  item: Item;
  groupPrices: ItemGroupPrice[];
  pricingEnabled: boolean;
  canCost: boolean;
  canWrite: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const locale = i18n.language;
  const [lists, setLists] = useState<PriceList[]>([]);

  useEffect(() => {
    if (!pricingEnabled) return undefined;
    let active = true;
    void listPriceLists().then((l) => { if (active) setLists(l.filter((x) => x.isActive)); }).catch(() => undefined);
    return () => { active = false; };
  }, [pricingEnabled]);

  const rows: Row[] = [
    {
      key: '__base__', unitId: null, name: item.unitOfMeasure, factor: 1, hidden: false,
      salePrice: item.salePrice, purchasePrice: item.purchasePrice,
    },
    ...item.units.map((u) => ({
      key: u.id, unitId: u.id, name: u.name, factor: Number(u.factor), hidden: !u.isActive,
      salePrice: u.salePrice, purchasePrice: u.purchasePrice,
    })),
  ];

  // The groups, in the order the API lists them (by name), with their rule.
  const groupIds = Array.from(new Set(groupPrices.map((g) => g.priceListId)));
  const groups = groupIds.map((id) => {
    const first = groupPrices.find((g) => g.priceListId === id)!;
    return { id, name: first.priceListName, currency: first.listCurrencyCode, list: lists.find((l) => l.id === id) };
  });
  const cellOf = (listId: string, unitId: string | null): ItemGroupPrice | undefined =>
    groupPrices.find((g) => g.priceListId === listId && (g.itemUnitId ?? null) === unitId);

  const price = (amount: string | null | undefined, currency: string | null | undefined, factor: number): JSX.Element => {
    if (!amount || !currency) {
      return <span className="text-fg-subtle">—</span>;
    }
    const perBase = factor > 1 ? formatMoney(String(Number(amount) / factor), { locale, currency }) : null;
    return (
      <span className="flex flex-col items-end leading-tight">
        <span className="font-mono font-semibold tabular-nums text-fg">{formatMoney(amount, { locale, currency })}</span>
        {perBase && (
          <span className="font-mono text-[11px] tabular-nums text-fg-muted">
            {t('profile.prices.perBase', { defaultValue: '≈ {{price}} / {{unit}}', price: perBase, unit: item.unitOfMeasure })}
          </span>
        )}
      </span>
    );
  };

  const percentRule = (l: PriceList | undefined): string | null => {
    if (!l || l.adjustmentType !== 'percent' || l.adjustmentValue == null) return null;
    const v = Number(l.adjustmentValue);
    return t('profile.prices.percentRule', {
      defaultValue: '{{value}}% on the normal price',
      value: `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmtQty(Math.abs(v), locale)}`,
    });
  };

  const th = 'px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-fg-muted';
  return (
    <Card className="overflow-hidden p-0">
      <div className="px-4 pt-4">
        <SectionTitle
          icon={Tags}
          end={canWrite ? (
            <Link to={`/inventory/items/${item.id}/edit`}>
              <Button size="sm" variant="secondary"><Pencil className="h-4 w-4" aria-hidden />{t('profile.prices.edit', { defaultValue: 'Edit prices' })}</Button>
            </Link>
          ) : undefined}
        >
          {t('profile.prices.title', { defaultValue: 'Prices by unit and customer group' })}
        </SectionTitle>
        <p className="mb-3 text-xs text-fg-muted">
          {t('profile.prices.hint', { defaultValue: 'Each price is shown in its own currency and never converted. An empty group cell means that group pays the normal price.' })}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-border bg-bg-subtle">
              <th className={`${th} text-start`}>{t('form.units.name', { defaultValue: 'Unit' })}</th>
              <th className={`${th} text-end`}>{t('profile.prices.contains', { defaultValue: 'Contains' })}</th>
              <th className={`${th} text-end`}>
                <span className="flex flex-col items-end">
                  <span>{t('profile.prices.normal', { defaultValue: 'Normal price' })}</span>
                  {item.saleCurrencyCode && <span className="font-mono text-[10px] font-normal text-fg-subtle">{item.saleCurrencyCode}</span>}
                </span>
              </th>
              {groups.map((g) => (
                <th key={g.id} className={`${th} text-end`}>
                  <span className="flex flex-col items-end gap-0.5">
                    <span className="normal-case tracking-normal text-fg">{g.name}</span>
                    <span className="flex items-center gap-1">
                      {g.currency && <span className="font-mono text-[10px] font-normal text-fg-subtle">{g.currency}</span>}
                      {g.list?.hideFromPos && (
                        <Badge variant="secondary" className="text-[10px]">{t('profile.prices.offTill', { defaultValue: 'Not at the till' })}</Badge>
                      )}
                    </span>
                  </span>
                </th>
              ))}
              {canCost && (
                <th className={`${th} text-end`}>
                  <span className="flex flex-col items-end">
                    <span>{t('profile.prices.purchase', { defaultValue: 'Purchase price' })}</span>
                    {item.purchaseCurrencyCode && <span className="font-mono text-[10px] font-normal text-fg-subtle">{item.purchaseCurrencyCode}</span>}
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={`border-b border-border last:border-0 ${r.hidden ? 'text-fg-muted' : ''}`}>
                <td className="px-3 py-2.5 font-medium">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {r.name}
                    {r.unitId === null && <Badge variant="secondary">{t('form.units.base', { defaultValue: 'Base unit' })}</Badge>}
                    {r.hidden && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
                        <EyeOff className="h-3 w-3" aria-hidden />{t('form.units.hidden', { defaultValue: 'Hidden' })}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-end font-mono tabular-nums text-fg-muted">
                  <span dir="ltr">×{fmtQty(r.factor, locale)}</span>
                  <span className="ms-1 font-sans text-xs">{item.unitOfMeasure}</span>
                </td>
                <td className="px-3 py-2.5 text-end">{price(r.salePrice, item.saleCurrencyCode, r.factor)}</td>
                {groups.map((g) => {
                  const c = cellOf(g.id, r.unitId);
                  const rule = !c?.unitPrice && r.unitId === null ? percentRule(g.list) : null;
                  return (
                    <td key={g.id} className="px-3 py-2.5 text-end">
                      {c?.unitPrice
                        ? price(c.unitPrice, c.currencyCode ?? c.listCurrencyCode, r.factor)
                        : rule
                          ? <span className="text-xs text-fg-muted">{rule}</span>
                          : <span className="text-fg-subtle" title={t('profile.prices.unset', { defaultValue: 'Not set — this group pays the normal price' })}>—</span>}
                    </td>
                  );
                })}
                {canCost && <td className="px-3 py-2.5 text-end">{price(r.purchasePrice, item.purchaseCurrencyCode, r.factor)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pricingEnabled && groups.length === 0 && (
        <p className="border-t border-border px-4 py-3 text-xs text-fg-muted">
          {t('profile.prices.noGroups', { defaultValue: 'No customer groups yet — every customer pays the normal price.' })}
        </p>
      )}
    </Card>
  );
}
